/**
 * BoundedUnionClipper
 *
 * Custom clipping that supports UNION of BOUNDED regions.
 * Each cut (box or plane) is defined by multiple planes, and we clip if
 * the fragment is inside ANY cut region (union of bounded regions).
 *
 * This solves the THREE.js limitation where you can only have:
 * - clipIntersection=true: intersection of ALL planes
 * - clipIntersection=false: union of individual planes (not bounded regions)
 */
class BoundedUnionClipper {
  constructor() {
    this.cuts = []; // Array of cut definitions
    this.maxCuts = 8; // Maximum number of cuts supported
    this.maxPlanesPerCut = 6; // Max planes per cut (box = 6)
    this.enabled = false;
    this.materials = new Set(); // Track modified materials
  }

  /**
   * Add a bounded cut region
   * @param {Object} cut - { origin: [x,y,z], normal: [x,y,z], width, height, depth }
   * @returns {number} Cut index
   */
  addCut(cut) {
    if (this.cuts.length >= this.maxCuts) {
      console.warn('[BoundedUnionClipper] Maximum cuts reached');
      return -1;
    }

    // Calculate the 5-6 planes that define this cut
    const planes = this.calculateCutPlanes(cut);

    this.cuts.push({
      ...cut,
      planes: planes
    });

    return this.cuts.length - 1;
  }

  /**
   * Update an existing cut
   */
  updateCut(index, cut) {
    if (index < 0 || index >= this.cuts.length) return;

    const planes = this.calculateCutPlanes(cut);
    this.cuts[index] = { ...cut, planes };
  }

  /**
   * Remove a cut by index
   */
  removeCut(index) {
    if (index < 0 || index >= this.cuts.length) return;
    this.cuts.splice(index, 1);
  }

  /**
   * Clear all cuts
   */
  clearCuts() {
    this.cuts = [];
  }

  /**
   * Calculate clipping planes for a cut region
   * Returns array of vec4 [normalX, normalY, normalZ, distance]
   */
  calculateCutPlanes(cut) {
    const planes = [];

    const origin = new THREE.Vector3(cut.origin[0], cut.origin[1], cut.origin[2]);
    const normal = new THREE.Vector3(cut.normal[0], cut.normal[1], cut.normal[2]).normalize();

    const width = cut.width || 1;
    const height = cut.height || 1;
    const depth = cut.depth || 0;

    // Calculate local axes
    const worldUp = Math.abs(normal.y) < 0.99 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const uAxis = new THREE.Vector3().crossVectors(worldUp, normal).normalize();
    const vAxis = new THREE.Vector3().crossVectors(normal, uAxis).normalize();

    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Plane normals must point OUTWARD from the cut region.
    // The shader clips if dist <= 0 for ALL planes (point is inside).
    // So normals point away from the inside, making inside points have negative distance.

    // Front plane - normal points forward (away from the cut region behind it)
    planes.push([normal.x, normal.y, normal.z, -normal.dot(origin)]);

    // Left boundary - normal points LEFT (outward, away from the inside)
    const leftEdge = origin.clone().sub(uAxis.clone().multiplyScalar(halfWidth));
    const leftNormal = uAxis.clone().negate();  // Points left (-U)
    planes.push([leftNormal.x, leftNormal.y, leftNormal.z, -leftNormal.dot(leftEdge)]);

    // Right boundary - normal points RIGHT (outward, away from the inside)
    const rightEdge = origin.clone().add(uAxis.clone().multiplyScalar(halfWidth));
    planes.push([uAxis.x, uAxis.y, uAxis.z, -uAxis.dot(rightEdge)]);  // Points right (+U)

    // Bottom boundary - normal points DOWN (outward, away from the inside)
    const bottomEdge = origin.clone().sub(vAxis.clone().multiplyScalar(halfHeight));
    const bottomNormal = vAxis.clone().negate();  // Points down (-V)
    planes.push([bottomNormal.x, bottomNormal.y, bottomNormal.z, -bottomNormal.dot(bottomEdge)]);

    // Top boundary - normal points UP (outward, away from the inside)
    const topEdge = origin.clone().add(vAxis.clone().multiplyScalar(halfHeight));
    planes.push([vAxis.x, vAxis.y, vAxis.z, -vAxis.dot(topEdge)]);  // Points up (+V)

    // Back plane for volumes (depth > 0) - normal points backward (away from front)
    if (depth > 1.1920929e-7) {  // float32 epsilon
      const backOrigin = origin.clone().sub(normal.clone().multiplyScalar(depth));
      const backNormal = normal.clone().negate();
      planes.push([backNormal.x, backNormal.y, backNormal.z, -backNormal.dot(backOrigin)]);
    }

    console.log('[BoundedUnionClipper] Cut planes calculated:', planes.length, 'planes, origin:', origin.toArray(), 'size:', width, 'x', height, 'x', depth);
    return planes;
  }

  /**
   * Get shader uniforms for the current cuts
   */
  getUniforms() {
    // Flatten cuts into uniform arrays
    const cutPlanes = [];
    const cutPlaneCounts = [];

    for (let i = 0; i < this.maxCuts; i++) {
      if (i < this.cuts.length) {
        const cut = this.cuts[i];
        cutPlaneCounts.push(cut.planes.length);

        for (let j = 0; j < this.maxPlanesPerCut; j++) {
          if (j < cut.planes.length) {
            cutPlanes.push(new THREE.Vector4(...cut.planes[j]));
          } else {
            cutPlanes.push(new THREE.Vector4(0, 0, 0, 0));
          }
        }
      } else {
        cutPlaneCounts.push(0);
        for (let j = 0; j < this.maxPlanesPerCut; j++) {
          cutPlanes.push(new THREE.Vector4(0, 0, 0, 0));
        }
      }
    }

    return {
      boundedClipEnabled: { value: this.enabled && this.cuts.length > 0 },
      numBoundedCuts: { value: this.cuts.length },
      cutPlanes: { value: cutPlanes },
      cutPlaneCounts: { value: cutPlaneCounts }
    };
  }

  /**
   * Generate the clipping shader code
   */
  getClippingShaderChunk() {
    return `
      #ifdef USE_BOUNDED_CLIPPING

      uniform bool boundedClipEnabled;
      uniform int numBoundedCuts;
      uniform vec4 cutPlanes[${this.maxCuts * this.maxPlanesPerCut}];
      uniform int cutPlaneCounts[${this.maxCuts}];

      bool isInsideBoundedCut(vec3 worldPos) {
        if (!boundedClipEnabled || numBoundedCuts == 0) return false;

        // Check each cut region
        for (int cutIdx = 0; cutIdx < ${this.maxCuts}; cutIdx++) {
          if (cutIdx >= numBoundedCuts) break;

          int planeCount = cutPlaneCounts[cutIdx];
          if (planeCount == 0) continue;

          bool insideThisCut = true;
          int planeOffset = cutIdx * ${this.maxPlanesPerCut};

          // Check all planes of this cut
          for (int planeIdx = 0; planeIdx < ${this.maxPlanesPerCut}; planeIdx++) {
            if (planeIdx >= planeCount) break;

            vec4 plane = cutPlanes[planeOffset + planeIdx];
            // Point is on negative side if dot(normal, point) + d < 0
            float dist = dot(plane.xyz, worldPos) + plane.w;

            if (dist > 0.0) {
              // Point is outside this plane, so outside this cut
              insideThisCut = false;
              break;
            }
          }

          if (insideThisCut) {
            // Point is inside this cut region - should be clipped
            return true;
          }
        }

        return false;
      }

      #endif
    `;
  }

  /**
   * Apply bounded clipping to a material
   */
  applyToMaterial(material) {
    if (this.materials.has(material)) return;

    // Skip MeshBasicMaterial - it's used for cutting plane visualizations
    // and doesn't need clipping (also avoids shader compilation issues)
    if (material.type === 'MeshBasicMaterial' || material.type === 'LineBasicMaterial') {
      return;
    }

    const self = this;

    material.onBeforeCompile = function (shader) {
      try {
        console.log('[BoundedUnionClipper] onBeforeCompile called for:', material.type);

        // Add uniforms
        const uniforms = self.getUniforms();
        Object.assign(shader.uniforms, uniforms);

        // Store reference for updates
        material.userData.boundedClipUniforms = shader.uniforms;

        // VERTEX SHADER modifications
        // 1. Add varying declaration before main
        if (shader.vertexShader.includes('void main() {')) {
          shader.vertexShader = shader.vertexShader.replace(
            'void main() {',
            `#ifdef USE_BOUNDED_CLIPPING
            varying vec3 vBoundedClipWorldPos;
            #endif
            void main() {`
          );
        }

        // 2. Calculate and pass world position to fragment shader
        // Try fog_vertex first, then project_vertex as fallback
        if (shader.vertexShader.includes('#include <fog_vertex>')) {
          shader.vertexShader = shader.vertexShader.replace(
            '#include <fog_vertex>',
            `#include <fog_vertex>
            #ifdef USE_BOUNDED_CLIPPING
            {
              vec4 bcWorldPos = modelMatrix * vec4(transformed, 1.0);
              vBoundedClipWorldPos = bcWorldPos.xyz;
            }
            #endif`
          );
        } else if (shader.vertexShader.includes('#include <project_vertex>')) {
          shader.vertexShader = shader.vertexShader.replace(
            '#include <project_vertex>',
            `#include <project_vertex>
            #ifdef USE_BOUNDED_CLIPPING
            {
              vec4 bcWorldPos = modelMatrix * vec4(transformed, 1.0);
              vBoundedClipWorldPos = bcWorldPos.xyz;
            }
            #endif`
          );
        }

        // FRAGMENT SHADER modifications
        if (shader.fragmentShader.includes('void main() {')) {
          shader.fragmentShader = shader.fragmentShader.replace(
            'void main() {',
            `#ifdef USE_BOUNDED_CLIPPING
            varying vec3 vBoundedClipWorldPos;
            ${self.getClippingShaderChunk()}
            #endif
            void main() {
              #ifdef USE_BOUNDED_CLIPPING
              if (isInsideBoundedCut(vBoundedClipWorldPos)) discard;
              #endif`
          );
        }

        console.log('[BoundedUnionClipper] Shader modified for material:', material.type);
      } catch (error) {
        console.error('[BoundedUnionClipper] Shader injection failed:', error);
      }
    };

    // Force shader recompilation:
    // 1. Add unique define to change the cache key
    // 2. Mark material as needing update
    // 3. Increment version to invalidate cache
    if (!material.defines) material.defines = {};
    material.defines.USE_BOUNDED_CLIPPING = '1';  // This also gets used by our #ifdef
    material.needsUpdate = true;

    // Force THREE.js to recompile by changing the material version
    // This ensures the onBeforeCompile hook gets called even if material was already rendered
    if (material.version !== undefined) {
      material.version++;
    }

    // Store original version to track changes
    material.userData.boundedClipVersion = Date.now();

    this.materials.add(material);
    console.log('[BoundedUnionClipper] Material registered:', material.type, 'uuid:', material.uuid.substring(0, 8), 'version:', material.version);
  }

  /**
   * Update uniforms on all applied materials
   */
  updateMaterials() {
    const uniforms = this.getUniforms();

    let updatedCount = 0;
    this.materials.forEach(material => {
      if (material.userData.boundedClipUniforms) {
        Object.keys(uniforms).forEach(key => {
          if (material.userData.boundedClipUniforms[key]) {
            material.userData.boundedClipUniforms[key].value = uniforms[key].value;
          }
        });
        updatedCount++;
      }
    });

    console.log('[BoundedUnionClipper] Updated uniforms on', updatedCount, 'materials. Enabled:', uniforms.boundedClipEnabled.value, 'Cuts:', uniforms.numBoundedCuts.value);
  }

  /**
   * Enable/disable clipping
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    this.updateMaterials();
  }

  /**
   * Remove clipping from a material
   */
  removeFromMaterial(material) {
    if (!this.materials.has(material)) return;

    material.onBeforeCompile = null;
    material.needsUpdate = true;
    this.materials.delete(material);
  }

  /**
   * Remove clipping from all materials
   */
  removeFromAllMaterials() {
    this.materials.forEach(material => {
      material.onBeforeCompile = null;
      material.needsUpdate = true;
    });
    this.materials.clear();
  }
}

// Export globally
window.BoundedUnionClipper = BoundedUnionClipper;
